import { Plus, Trash2 } from "lucide-react";
import type { PickupTimeWindow, PickupWeekday } from "@gaole/shared";
import { PICKUP_WEEKDAY_LABELS } from "@gaole/shared";
import { Button } from "../../components/ui";

const ALL_WEEKDAYS: PickupWeekday[] = [0, 1, 2, 3, 4, 5, 6];

function emptyWindow(): PickupTimeWindow {
  return { days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" };
}

export function PickupAvailabilityEditor({
  value,
  onChange,
}: {
  value: PickupTimeWindow[] | undefined;
  onChange: (windows: PickupTimeWindow[] | undefined) => void;
}) {
  const windows = value ?? [];

  function updateWindow(index: number, patch: Partial<PickupTimeWindow>) {
    const next = windows.map((w, i) => (i === index ? { ...w, ...patch } : w));
    onChange(next.length ? next : undefined);
  }

  function toggleDay(index: number, day: PickupWeekday) {
    const w = windows[index];
    if (!w) return;
    const days = w.days.includes(day) ? w.days.filter((d) => d !== day) : [...w.days, day].sort();
    updateWindow(index, { days: days as PickupWeekday[] });
  }

  function addWindow() {
    onChange([...windows, emptyWindow()]);
  }

  function removeWindow(index: number) {
    const next = windows.filter((_, i) => i !== index);
    onChange(next.length ? next : undefined);
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold text-foreground">Pickup hours</p>
        <p className="mt-0.5 text-[11px] text-muted">
          Optional. When set, customers can only choose times within these windows. Leave empty for
          any time within the global lead time.
        </p>
      </div>

      {windows.length === 0 ? (
        <p className="text-xs text-muted">No restricted hours — customers pick any available slot.</p>
      ) : (
        windows.map((w, index) => (
          <div key={index} className="space-y-2 rounded-xl border border-line bg-surface p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Window {index + 1}</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-red-400"
                onClick={() => removeWindow(index)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {ALL_WEEKDAYS.map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(index, day)}
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                    w.days.includes(day)
                      ? "border-brand-pink/50 bg-brand-pink/10 text-brand-pink"
                      : "border-line bg-surface-2 text-muted hover:text-foreground"
                  }`}
                >
                  {PICKUP_WEEKDAY_LABELS[day]}
                </button>
              ))}
            </div>
            <div className="form-row-2">
              <label className="block text-xs">
                <span className="mb-1 block text-muted">From</span>
                <input
                  type="time"
                  value={w.start}
                  onChange={(e) => updateWindow(index, { start: e.target.value })}
                  className="w-full rounded-xl border border-line bg-input-bg px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted">Until</span>
                <input
                  type="time"
                  value={w.end}
                  onChange={(e) => updateWindow(index, { end: e.target.value })}
                  className="w-full rounded-xl border border-line bg-input-bg px-3 py-2 text-sm text-foreground"
                />
              </label>
            </div>
          </div>
        ))
      )}

      <Button type="button" size="sm" variant="outline" onClick={addWindow}>
        <Plus size={14} className="mr-1" />
        Add hours window
      </Button>
    </div>
  );
}
